"use client"

import cynamonowaImage from '../../../assets/cynamonowa.png';
import lanciegoImage from '../../../assets/lanciego.png';
import { Box, Container, Grid, Heading, HStack, Image, Stack, Text, VStack } from '@chakra-ui/react';
import { LuClock, LuMapPin } from 'react-icons/lu';

interface LocationProps {
  id: string
  name: string
  address: string
  directions: string
  hours: string
  imageUrl: string
  embedMapUrl: string
}

const locations: LocationProps[] = [
  {
    id: "lanciego",
    name: "Warszawa – ul. Franciszka Marii Lanciego",
    address: "Franciszka Marii Lanciego 7/16, 02-792 Warszawa",
    directions: "5 min spacerem od stacji metra Natolin",
    hours: "poniedziałek - piątek: 8:00 - 20:00\nsobota: 9:00 - 16:00",
    imageUrl: lanciegoImage,
    embedMapUrl: "https://www.google.com/maps/embed/v1/place?key=AIzaSyCatA9aqqRlz-q30ryAqrUfm88Hag2DaPQ&q=Centrum+Korepetycji+Opanuj.to&zoom=15&language=pl",
  },
  {
    id: "cynamonowa",
    name: "Warszawa – ul. Cynamonowa",
    address: "Cynamonowa 47, 02-777 Warszawa",
    directions: "przy KEN Center, 5 min spacerem od stacji metra Imielin lub stacji Stokłosy",
    hours: "poniedziałek - piątek: 8:00 - 20:00\nsobota: 9:00 - 16:00",
    imageUrl: cynamonowaImage,
    embedMapUrl: "https://www.google.com/maps?q=Cynamonowa%2047%2C%2002-777%20Warszawa&z=15&output=embed&hl=pl",
  }
]

const LocationCard = ({ location }: { location: LocationProps }) => (
  <VStack align="stretch" gap={0} borderRadius="lg" overflow="hidden" boxShadow="md" bg="white">
    {/* Image Section */}
    <Box position="relative" h="360px" w="full" overflow="hidden">
      <Image
        src={location.imageUrl}
        alt={location.name}
        w="full"
        h="full"
        objectFit="cover"
        transition="transform 0.3s ease"
        _hover={{ transform: "scale(1.05)" }}
      />
    </Box>

    {/* Content Section */}
    <Stack p={6} gap={5} flex={1}>
      <Box>
        <Heading as="h3" size="lg" mb={2}>
          {location.name}
        </Heading>
      </Box>

      {/* Address */}
      <HStack align="flex-start" gap={3}>
        <Box fontSize="xl" color="blue.500" flexShrink={0}>
          <LuMapPin />
        </Box>
        <VStack align="start" gap={0}>
          <Text fontWeight="600" fontSize="sm">
            Adres
          </Text>
          <Text fontSize="sm" color="gray.600">
            {location.address}
          </Text>
          <Text fontStyle="italic" fontSize="sm" color="gray.600" lineHeight="1.6">
            {location.directions}
          </Text>
        </VStack>
      </HStack>

      {/* Hours */}
      <HStack align="flex-start" gap={3}>
        <Box fontSize="xl" color="green.500" flexShrink={0}>
          <LuClock />
        </Box>
        <VStack align="start" gap={0}>
          <Text fontWeight="600" fontSize="sm">
            Godziny otwarcia
          </Text>
          <Text fontSize="sm" color="gray.600" whiteSpace="pre-line" lineHeight="1.6">
            {location.hours}
          </Text>
        </VStack>
      </HStack>
    </Stack>

    {/* Map Section */}
    <Box w="full" h="300px">
      <iframe
        width="100%"
        height="100%"
        style={{ border: 0 }}
        loading="lazy"
        allowFullScreen
        src={location.embedMapUrl}
        title={`Mapa dla ${location.name}`}
      />
    </Box>
  </VStack>
)

export const HomeLocations = () => {
  return (
    <Box id="placowki" as="section" py={{ base: 12, md: 16 }} bg="gray.50">
      <Container maxW="7xl">
        <VStack align="stretch" gap={12}>
          {/* Header */}
          <VStack align="center" gap={3} textAlign="center">
            <Text color="brand.orange.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
              Nasze placówki
            </Text>
          </VStack>

          {/* Locations Grid */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(2, 1fr)" }}
            gap={8}
            w="full"
          >
            {locations.map((location) => (
              <LocationCard key={location.id} location={location} />
            ))}
          </Grid>
        </VStack>
      </Container>
    </Box>
  )
}
